// SPDX-FileCopyrightText: 2024 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { nativeScrollViewProps } from 'components/common/scroll/TreactNativeScrollStyles';
import { ThreeSectorContent, TwoRowCell } from '@tls/treact-ui-templates';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import RoleDetails from './roles/RoleDetails';
import PageContent from 'components/treactui-template/organisms/page/PageContent';
import SelectRole from './roles/SelectRole';
import useMediaQuery from 'hooks/useMediaQuery';
import useRolesApi from 'hooks/useRolesApi';
import useFilteredRoles from 'hooks/useFilteredRoles';
import { Role } from 'model/Role';
import { Table } from '@tls/treact-ui';

export default function Roles() {
  const { roles, loadingRoles, deleteRole, editRole, permissions } = useRolesApi();
  const { t } = useTranslation();

  const filteredRoles = useFilteredRoles(roles);

  const renderTableRowCells = useCallback((role: Role) => {
    return [
      <Table.Cell key='Role' width='15rem' data-testid={`settings-role-${role.id ?? 'unknown'}-row-name`}>
        <TwoRowCell row1={role.name} row2={role.permissions?.join(' ,')} row1Bold />
      </Table.Cell>,
    ];
  }, []);

  const { isMedium, isSmall, isExtraSmall } = useMediaQuery();

  return (
    <PageContent displayAsPaper disableScroll>
      <ThreeSectorContent
        scrollViewProps={nativeScrollViewProps}
        isSmallWidth={isMedium || isSmall || isExtraSmall}
        selectionAdapters={[
          {
            listElementKeySelector: (role: Role) => role.id ?? '',
            menuLabel: 'role',
            tableHeaderCells: [
              <Table.HeaderCell key='Role' width='15rem'>
                {t('role.roleName')}
              </Table.HeaderCell>,
            ],
            listElements: filteredRoles ?? [],
            renderTableRowCells: renderTableRowCells,
            renderDetail: (listElement: Role, handleBack) => {
              if (!listElement) return <SelectRole />;
              return (
                <RoleDetails
                  role={listElement}
                  roles={roles}
                  isLoading={loadingRoles}
                  addOrEditRole={editRole}
                  deleteRole={deleteRole}
                  onClose={isMedium || isSmall || isExtraSmall ? handleBack : undefined}
                  permissions={permissions}
                  isBuiltInRole={listElement.isBuiltIn}
                />
              );
            },
          },
        ]}
      />
    </PageContent>
  );
}
